import React, { useState, useEffect, useRef } from 'react';
import type { LifeSummary } from '@gamengayxua/shared';
import {
  generateLifeCardCanvas,
  downloadLifeCardPng,
  shareLifeCardTelegram,
  type CardAspectRatio,
} from '../../utils/exportLifeImage.ts';

interface LifeSummaryShareCardProps {
  summary: LifeSummary;
  onClose?: () => void;
}

export const LifeSummaryShareCard: React.FC<LifeSummaryShareCardProps> = ({ summary, onClose }) => {
  const [aspectRatio, setAspectRatio] = useState<CardAspectRatio>('1:1');
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Generate Preview when summary or aspectRatio changes
  useEffect(() => {
    let isMounted = true;
    setIsGenerating(true);

    generateLifeCardCanvas(summary, aspectRatio)
      .then((canvas) => {
        if (isMounted) {
          setPreviewDataUrl(canvas.toDataURL('image/png', 0.95));
          setIsGenerating(false);
        }
      })
      .catch((err) => {
        console.error('Error generating preview:', err);
        if (isMounted) setIsGenerating(false);
      });

    return () => {
      isMounted = false;
    };
  }, [summary, aspectRatio]);

  const handleDownload = async () => {
    try {
      setIsGenerating(true);
      await downloadLifeCardPng(summary, aspectRatio);
      setFeedback('✅ Đã tải ảnh PNG chất lượng cao về máy thành công!');
      setTimeout(() => setFeedback(null), 3000);
    } catch (err: any) {
      setFeedback(`Lỗi khi lưu ảnh: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShare = () => {
    shareLifeCardTelegram(summary);
  };

  return (
    <div className="retro-card p-4 space-y-4 text-left border-amber-500/30 shadow-2xl">
      {/* Header Modal / Card Title */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-3">
        <div className="flex items-center space-x-2.5">
          <span className="text-3xl">📸</span>
          <div>
            <h3 className="text-lg sm:text-xl font-black text-amber-400 uppercase tracking-wide">
              XUẤT ẢNH TỔNG KẾT CUỘC ĐỜI
            </h3>
            <p className="text-sm text-slate-200 font-medium">
              Ảnh PNG độ nét cao lưu giữ hành trình nhân sinh
            </p>
          </div>
        </div>

        {onClose && (
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg bg-slate-800 text-slate-200 hover:text-white flex items-center justify-center text-base font-bold"
          >
            ✕
          </button>
        )}
      </div>

      {/* Ratio Selector Buttons */}
      <div className="flex items-center space-x-2 bg-slate-900/90 p-2.5 rounded-xl border border-slate-800 text-sm sm:text-base">
        <button
          onClick={() => setAspectRatio('1:1')}
          className={`flex-1 py-3 rounded-lg font-black transition-all flex items-center justify-center space-x-2 ${
            aspectRatio === '1:1'
              ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <span className="text-lg">⏹️</span>
          <span>Khung Vuông (1:1)</span>
        </button>

        <button
          onClick={() => setAspectRatio('9:16')}
          className={`flex-1 py-3 rounded-lg font-black transition-all flex items-center justify-center space-x-2 ${
            aspectRatio === '9:16'
              ? 'bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <span className="text-lg">📱</span>
          <span>Khung Dọc Story (9:16)</span>
        </button>
      </div>

      {/* Image Preview Canvas Display */}
      <div className="bg-[#050811] p-3 rounded-2xl border border-slate-800/80 flex items-center justify-center min-h-[300px] overflow-hidden relative shadow-inner">
        {isGenerating && (
          <div className="absolute inset-0 bg-[#050811]/80 backdrop-blur-sm flex flex-col items-center justify-center space-y-2 z-10">
            <div className="w-8 h-8 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-base text-amber-300 font-bold">Đang vẽ ảnh PNG độ nét cao...</span>
          </div>
        )}

        {previewDataUrl ? (
          <img
            src={previewDataUrl}
            alt="Life Summary Card Preview"
            className={`rounded-xl shadow-2xl border border-slate-800 object-contain max-h-[460px] w-auto transition-transform ${
              aspectRatio === '1:1' ? 'aspect-square' : 'aspect-[9/16]'
            }`}
          />
        ) : (
          <span className="text-sm text-slate-400">Đang chuẩn bị khung hình...</span>
        )}
      </div>

      {/* Catholic Identity & Highlight Specs Box */}
      <div className="bg-slate-900/90 p-4 rounded-xl border border-slate-800 space-y-2.5 text-sm sm:text-base">
        <div className="flex items-center space-x-2 text-cyan-300 font-bold text-base sm:text-lg">
          <span>✝</span>
          <span>Bản Sắc Đức Tin & Công Giáo:</span>
        </div>
        <p className="text-slate-200 leading-normal">
          • Tên thánh & Bổn mạng: <strong className="text-white font-bold">{summary.saintName || 'Giuse'}</strong> ({summary.faithLife?.patronSaintVi || 'Thánh Bổn Mạng'})
        </p>
        <p className="text-slate-200 leading-normal">
          • Các Bí Tích đã lãnh nhận:{' '}
          <span className="text-cyan-300 font-bold">
            {(summary.faithLife?.sacramentsReceived || ['Bí Tích Rửa Tội', 'Bí Tích Thánh Thể', 'Bí Tích Thêm Sức', 'Bí Tích Hôn Phối', 'Bí Tích Xức Dầu Bệnh Nhân']).join(', ')}
          </span>
        </p>
        <p className="text-slate-200 leading-normal">
          • Nguyên nhân qua đời: <span className="text-rose-300 font-semibold">{summary.causeOfDeathVi || 'Tuổi cao sức yếu, tạ thế bình yên'}</span>
        </p>
      </div>

      {/* Feedback Toast */}
      {feedback && (
        <div className="p-3.5 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-base text-center font-bold">
          {feedback}
        </div>
      )}

      {/* Action Buttons: Download & Share */}
      <div className="grid grid-cols-2 gap-3 pt-1">
        <button
          onClick={handleDownload}
          disabled={isGenerating}
          className="py-4 px-3 rounded-xl font-black text-sm sm:text-base retro-button flex items-center justify-center space-x-2 shadow-md shadow-amber-500/20 active:scale-95 transition-transform uppercase"
        >
          <span className="text-lg">📥</span>
          <span>LƯU ẢNH (PNG)</span>
        </button>

        <button
          onClick={handleShare}
          className="py-4 px-3 rounded-xl font-black text-sm sm:text-base retro-button-cyan flex items-center justify-center space-x-2 shadow-md shadow-cyan-500/20 active:scale-95 transition-transform uppercase"
        >
          <span className="text-lg">✈️</span>
          <span>CHIA SẺ TELEGRAM</span>
        </button>
      </div>
    </div>
  );
};
